import { GetDataKindResponse } from "../../services/data-kinds";
import { GetProjectResponse } from "../../services/project";
import { GetIfDefinitionForReferenceResult } from "../../services/if-definitions";
import { LoginUserRole } from "../../services/data-list/get-login-user-role-list/GetLoginUserRoleListResponse";
import {
  Information,
  Project,
  ProjectTcktInfo,
} from "../../services/if-definitions/get-if-definitions/GetIFDefinitionsResponse";
import { DisplayColumn, ErrorMessage } from "../../util/spreadsheet-data-utils";

export type InterfaceRequirementListPageState = {
  dfnSetVerNum: string;
  result: {
    total: number;
    ifList: InterfacefRequirement[];
  };
  dataKind: GetDataKindResponse;
  project?: GetProjectResponse;
  ifDefinitionReferenceList: GetIfDefinitionForReferenceResult[];
  codes: {
    inDefCommonFlagType?: Option[];
    ptnrType?: Option[];
    ptnrBuCntryType?: Option[];
    interfaceRequirementType?: Option[];
    securityInformationLevelType?: Option[];
    encriptionType?: Option[];
    snapshotIncrementalDeltaType?: Option[];
    ifCycleType?: Option[];
    dataScopeType?: Option[];
    fileTypeType?: Option[];
    ediPollingAdaptorType?: Option[];
    sendDatalakeType?: Option[];
    ifReqCnclType?: Option[];
    dipByRegionList?: Option[];
    inboundEdhProtocolList?: Option[];
    outboundEdhProtocolList?: Option[];
    srcEdiChainTypeList?: Option[];
    dstEdiChainTypeList?: Option[];
  };
  displayColumn: DisplayColumn[];
  status: {
    invalidList: string[];
    invalidRecordList: string[];
    loadedCount?: number;
  };
  spreadsheet: {
    selectRowIdx?: number;
    tmplateRow: SpreadsheetRow;
  };
  infoBar: InfoBar;
  loginUserRoleList: LoginUserRole[];
  authorized: boolean;
};

export type InterfacefRequirement = {
  updateDateTime: string;
  information: Information;
  projectTcktInfo: ProjectTcktInfo;
  project: Project;
  src: Src;
  dst: Dst;
};

type Src = {
  interfaceDefinition: InterfaceDefinition;
};

type Dst = {
  interfaceDefinition: InterfaceDefinition;
};

type InterfaceDefinition = {
  interfaceRequirementType?: string;
  metaOnlyFlag?: string;
  interfaceId?: string;
  interfaceNameEnglish?: string;
  interfaceNameJapanese?: string;
  dataKindId?: string;
  dipByRegion?: string;
  inbound?: Inbound;
  outbound?: Outbound;
};

type Inbound = {
  inboundId?: string;
  inboundIdOptionalPart?: string;
  componentId?: string;
  protocol?: string;
  securityInformationLevel?: string;
  useEtlTransceiver?: string;
  useConverter?: string;
  inboundConverter?: InboundConverter;
  inboundPartner?: InboundPartner;
  inboundMeta?: InboundMeta;
  inboundSecurity?: InboundSecurity;
};

type Outbound = {
  outboundId?: string;
  outboundIdOptionalPart?: string;
  componentId?: string;
  protocol?: string;
  sendDatalakeType?: string;
  useEtlTransceiver?: string;
  useConverter?: string;
  outboundConverter?: OutboundConverter;
  outboundPartner?: OutboundPartner;
  outboundMeta?: OutboundMeta;
  outboundSecurity?: OutboundSecurity;
};

type InboundConverter = {
  differentLayoutsForInputAndOutput?: string;
};

type OutboundConverter = {
  differentLayoutsForInputAndOutput?: string;
};

type InboundPartner = {
  partnerIfId?: string;
  partnerCode?: string;
  partnerName?: string;
  partnerType?: string;
  partnerBusiness?: string;
  chainTypeCode?: string;
  fileTypeCode?: string;
  pollingAdaptorTypeCode?: string;
};

type OutboundPartner = {
  partnerIfId?: string;
  partnerCode?: string;
  partnerName?: string;
  partnerType?: string;
  partnerBusiness?: string;
  chainTypeCode?: string;
  fileTypeCode?: string;
};

type InboundMeta = {
  ifCycle?: string;
  ifCycleNote?: string;
  snapshotIncrementalDelta?: string;
  numberOfFilesPerASendProcess?: string;
  densityOfNumberOfFilesPerAMinute?: string;
  dataScopePerInterfaceId?: string;
  dataScopePerInterfaceIdNote?: string;
  dataScopePerInterfaceFile?: string;
  dataScopePerInterfaceFileNote?: string;
};

type OutboundMeta = {
  dataScopePerInterfaceId?: string;
  dataScopePerInterfaceIdNote?: string;
  dataScopePerInterfaceFile?: string;
  dataScopePerInterfaceFileNote?: string;
};

type InboundSecurity = {
  encryptionTypeKey?: string;
  encryptionTypeValue?: string;
};

type OutboundSecurity = {
  encryptionTypeKey?: string;
  encryptionTypeValue?: string;
};

export type Option = {
  label: string;
  value: string;
};

export type SpreadsheetState = {
  results: SpreadsheetRow[];
  mode?: {
    readonly: boolean;
    isChanging: boolean;
  };
  codes?: {
    inDefCommonFlagType: Option[];
    ptnrType: Option[];
    ptnrBuCntryType: Option[];
    interfaceRequirementType: Option[];
    securityInformationLevelType: Option[];
    encriptionType: Option[];
    snapshotIncrementalDeltaType: Option[];
    ifCycleType: Option[];
    dataScopeType: Option[];
    fileTypeType: Option[];
    ediPollingAdaptorType: Option[];
    sendDatalakeType: Option[];
    ifReqCnclType: Option[];
    dipByRegionList: Option[];
    inboundEdhProtocolList: Option[];
    outboundEdhProtocolList: Option[];
    srcEdiChainTypeList: Option[];
    dstEdiChainTypeList: Option[];
  };
};

export type CellItem = {
  rowIdx: number;
  col: string;
};

export type SpreadsheetRow = {
  isAddRow: boolean;
  cancelledRequirement?: boolean;
  updateFlag: boolean;
  updateColumn: {
    key: string;
    value: string;
  }[];
  updateDateTime?: string;
  project?: Project;
  src?: Src;
  dst?: Dst;
  information?: Information;
  projectTcktInfo?: ProjectTcktInfo;
  require: CellStyle;
  editable: CellStyle;
  errorColumnList: string[];
  errorMessage: string;
  messageList: ErrorMessage[];
};

export type CellStyle = {
  "project.ifReqId": string;
  "project.ifReqCnclTypeCd": string;
  "src.interfaceDefinition.interfaceRequirementType": string;
  "dst.interfaceDefinition.interfaceRequirementType": string;
  "project.dtKindIdIssueNumGrpCd": string;
  "src.interfaceDefinition.dataKindId": string;
  "src.interfaceDefinition.interfaceNameEnglish": string;
  "src.interfaceDefinition.interfaceNameJapanese": string;
  "project.mltcstDsbldFlg": string;
  "src.interfaceDefinition.dipByRegion": string;
  "src.interfaceDefinition.inbound.componentId": string;
  "src.interfaceDefinition.interfaceId": string;
  "project.exgInbdId": string;
  "src.interfaceDefinition.inbound.inboundIdMandatoryPart": string;
  "src.interfaceDefinition.inbound.inboundIdOptionalPart": string;
  "src.interfaceDefinition.inbound.inboundPartner.partnerType": string;
  "src.interfaceDefinition.inbound.inboundPartner.partnerCode": string;
  "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness": string;
  "src.interfaceDefinition.outbound.outboundId": string;
  "src.interfaceDefinition.outbound.outboundIdOptionalPart": string;
  "dst.interfaceDefinition.dipByRegion": string;
  "dst.interfaceDefinition.outbound.componentId": string;
  "dst.interfaceDefinition.interfaceId": string;
  "project.exgOtbdId": string;
  "dst.interfaceDefinition.outbound.outboundIdMandatoryPart": string;
  "dst.interfaceDefinition.outbound.outboundIdOptionalPart": string;
  "dst.interfaceDefinition.outbound.outboundPartner.partnerType": string;
  "dst.interfaceDefinition.outbound.outboundPartner.partnerCode": string;
  "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness": string;
  "project.stgDplymntPlnDate": string;
  "project.prdDplymntPlnDate": string;
  "src.interfaceDefinition.inbound.securityInformationLevel": string;
  "src.interfaceDefinition.inbound.inboundSecurity.inboundEncryptionTypeValue": string;
  "dst.interfaceDefinition.outbound.outboundSecurity.outboundEncryptionTypeValue": string;
  "src.interfaceDefinition.inbound.inboundMeta.ifCycle": string;
  "src.interfaceDefinition.inbound.inboundMeta.ifCycleNote": string;
  "src.interfaceDefinition.inbound.inboundMeta.numberOfFilesPerASendProcess": string;
  "src.interfaceDefinition.inbound.inboundMeta.densityOfNumberOfFilesPerAMinute": string;
  "src.interfaceDefinition.inbound.inboundMeta.snapshotIncrementalDelta": string;
  "src.interfaceDefinition.inbound.inboundMeta.dataScopePerInterfaceId": string;
  "src.interfaceDefinition.inbound.inboundMeta.dataScopePerInterfaceIdNote": string;
  "src.interfaceDefinition.inbound.inboundMeta.dataScopePerInterfaceFile": string;
  "src.interfaceDefinition.inbound.inboundMeta.dataScopePerInterfaceFileNote": string;
  "src.interfaceDefinition.inbound.protocol": string;
  "src.interfaceDefinition.inbound.useEtlTransceiver": string;
  "src.interfaceDefinition.inbound.useConverter": string;
  "src.interfaceDefinition.inbound.inboundConverter.differentLayoutsForInputAndOutput": string;
  "src.interfaceDefinition.inbound.inboundPartner.chainTypeCode": string;
  "src.interfaceDefinition.inbound.inboundPartner.fileTypeCode": string;
  "src.interfaceDefinition.inbound.inboundPartner.pollingAdaptorTypeCode": string;
  "src.interfaceDefinition.inbound.inboundPartner.partnerIfId": string;
  "dst.interfaceDefinition.outbound.outboundMeta.dataScopePerInterfaceId": string;
  "dst.interfaceDefinition.outbound.outboundMeta.dataScopePerInterfaceIdNote": string;
  "dst.interfaceDefinition.outbound.outboundMeta.dataScopePerInterfaceFile": string;
  "dst.interfaceDefinition.outbound.outboundMeta.dataScopePerInterfaceFileNote": string;
  "dst.interfaceDefinition.outbound.protocol": string;
  "dst.interfaceDefinition.outbound.sendDatalakeType": string;
  "dst.interfaceDefinition.outbound.useEtlTransceiver": string;
  "dst.interfaceDefinition.outbound.useConverter": string;
  "dst.interfaceDefinition.outbound.outboundConverter.differentLayoutsForInputAndOutput": string;
  "dst.interfaceDefinition.outbound.outboundPartner.chainTypeCode": string;
  "dst.interfaceDefinition.outbound.outboundPartner.fileTypeCode": string;
  "dst.interfaceDefinition.outbound.outboundPartner.partnerIfId": string;
  "dst.interfaceDefinition.outbound.autoRetryFlg": string;
  "src.interfaceDefinition.metaOnlyFlag": string;
  "dst.interfaceDefinition.metaOnlyFlag": string;
};

export type SpreadsheetMessage = {
  messageId: string;
  message: string;
  column: string;
};

export type InfoBar = {
  isShow: boolean;
  message: string;
  ready: string;
};

export type RecordSet = {
  [K in keyof CellStyle]: {
    srcDst: string;
    C: string;
    R: string;
    U: string;
    D: string;
    INIT: string;
  };
};

export type PageNewState =
  | InterfaceRequirementListPageState
  | ((
      prevState: InterfaceRequirementListPageState,
    ) => InterfaceRequirementListPageState);
